import React, { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFile, faFilePdf, faFileLines, faDownload, faTrash, faPen, faCheck, faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { SiteContext } from '../../Contexts';
import { FileItem, OrderDirection } from '../../Models/Models';
import { FileService } from '../../Services/FileService';
import { EntityColors } from '../../Themes/EntityColors';
import { Utils } from '../../Services/Utils';
import TableShell from '../Table/TableShell';
import TableToolbar from '../Table/TableToolbar';
import FileDeleteModal from '../Modals/FileDeleteModal';
import UserAvatarCell from '../Avatar/UserAvatarCell';

const FileTableComponent: React.FC = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Súbory');
    }, []);

    const { filterFiles, deleteFile, editFileName } = FileService();
    const { currentSite }: any = useContext(SiteContext);
    const { action, icon } = EntityColors.file;

    const [files, setFiles] = useState<FileItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [orderDirection, setOrderDirection] = useState<OrderDirection>('Desc');
    const [loading, setLoading] = useState(true);
    const [showDelete, setShowDelete] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<FileItem | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const load = async (pageNum: number, opts?: { perPage?: number; order?: OrderDirection }) => {
        if (currentSite.id === -1) return;
        setLoading(true);
        const limit = opts?.perPage ?? perPage;
        const data = await filterFiles({
            websiteId: currentSite.id,
            limit,
            offset: (pageNum - 1) * limit,
            orderDirection: opts?.order ?? orderDirection,
        });
        if (data) {
            setFiles(data.files ?? []);
            setTotalCount(data.totalCount ?? 0);
        }
        setLoading(false);
    };

    useEffect(() => { void load(1); setPage(1); }, [currentSite, refreshTrigger]);

    const handlePageChange = (p: number) => {
        setPage(p);
        void load(p);
    };

    const changePerPage = (count: number) => {
        setPerPage(count);
        setPage(1);
        void load(1, { perPage: count });
    };

    const changeOrder = (dir: OrderDirection) => {
        setOrderDirection(dir);
        setPage(1);
        void load(1, { order: dir });
    };

    const startDelete = (file: FileItem) => {
        setFileToDelete(file);
        setShowDelete(true);
    };

    const confirmDelete = async () => {
        if (!fileToDelete) return;
        await deleteFile(fileToDelete.id)
            .then(() => { setShowDelete(false); setFileToDelete(null); setRefreshTrigger(r => r + 1); })
            .catch(() => { setShowDelete(false); });
    };

    const handleRename = async (file: FileItem, newFileName: string) => {
        await editFileName(file.id, newFileName)
            .then(() => setRefreshTrigger(r => r + 1))
            .catch(() => {});
    };

    return (
        <>
            <TableShell
                title="Súbory"
                icon={icon}
                accentColor={action}
                createHref="/file/add"
                createLabel="Nahrať"
                isLoading={loading}
                isEmpty={!loading && files.length === 0}
                emptyMessage="Žiadne súbory"
                page={page}
                totalCount={totalCount}
                perPage={perPage}
                onPageChange={handlePageChange}
                toolbar={
                    <TableToolbar
                        totalCount={totalCount}
                        nounForms={['súbor', 'súbory', 'súborov']}
                        perPage={perPage}
                        onPerPageChange={changePerPage}
                        pageSizes={[15, 30, 60]}
                        orderDirection={orderDirection}
                        onOrderChange={changeOrder}
                    />
                }
            >
                <table>
                    <thead>
                        <tr>
                            <th>Súbor</th>
                            <th>Typ</th>
                            <th>Vytvorené</th>
                            <th>Vytvoril</th>
                            <th>Naposledy upravené</th>
                            <th>Upravil</th>
                            <th style={{ width: '1%' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {files.map(file => (
                            <FileRow
                                key={file.id}
                                file={file}
                                accentColor={action}
                                onDelete={() => startDelete(file)}
                                onRename={newName => handleRename(file, newName)}
                            />
                        ))}
                    </tbody>
                </table>
            </TableShell>

            <FileDeleteModal
                show={showDelete}
                onHide={() => { setShowDelete(false); setFileToDelete(null); }}
                onSubmit={confirmDelete}
                idToDelete={fileToDelete?.id ?? 0}
                fileName={fileToDelete?.fileName}
            />
        </>
    );
};

const FileRow: React.FC<{
    file: FileItem;
    accentColor: string;
    onDelete: () => void;
    onRename: (newFileName: string) => Promise<void>;
}> = ({ file, accentColor, onDelete, onRename }) => {
    const [hovered, setHovered] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);

    const dotIndex = file.fileName.lastIndexOf('.');
    const baseName = dotIndex > 0 ? file.fileName.slice(0, dotIndex) : file.fileName;
    // Extension (including the dot) is fixed - renaming can never change the file's type.
    const extension = dotIndex > 0 ? file.fileName.slice(dotIndex) : '';
    const ext = extension.slice(1).toLowerCase();

    const [editValue, setEditValue] = useState(baseName);

    const { fileIcon, iconColor } = getFileStyle(ext);

    const startEditing = () => {
        setEditValue(baseName);
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setEditValue(baseName);
    };

    const saveEditing = async () => {
        const trimmed = editValue.trim();
        if (!trimmed || trimmed === baseName) {
            setIsEditing(false);
            return;
        }
        setSaving(true);
        await onRename(trimmed + extension);
        setSaving(false);
        setIsEditing(false);
    };

    return (
        <tr>
            <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        backgroundColor: iconColor + '18',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <FontAwesomeIcon icon={fileIcon} style={{ color: iconColor, fontSize: '0.95rem' }} />
                    </div>
                    {isEditing ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flex: 1, minWidth: 0 }}>
                            <input
                                type="text"
                                value={editValue}
                                autoFocus
                                disabled={saving}
                                onChange={e => setEditValue(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') void saveEditing();
                                    if (e.key === 'Escape') cancelEditing();
                                }}
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontWeight: 500,
                                    color: '#0F172A',
                                    border: `1.5px solid ${accentColor}`,
                                    borderRadius: '6px',
                                    padding: '0.25rem 0.5rem',
                                }}
                            />
                            {extension && (
                                <span title="Typ súboru sa pri premenovaní nedá zmeniť" style={{
                                    fontWeight: 500,
                                    color: '#94A3B8',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {extension}
                                </span>
                            )}
                        </div>
                    ) : (
                        <span style={{ fontWeight: 500, color: '#0F172A', wordBreak: 'break-all' }}>
                            {file.fileName}
                        </span>
                    )}
                </div>
            </td>
            <td>
                <span style={{
                    display: 'inline-block',
                    padding: '0.2rem 0.55rem',
                    borderRadius: '99px',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    backgroundColor: iconColor + '18',
                    color: iconColor,
                }}>
                    {ext || '—'}
                </span>
            </td>
            <td style={{ whiteSpace: 'nowrap' }}>
                {file.createdAt ? Utils.formatDate(file.createdAt) : '—'}
            </td>
            <td>
                <UserAvatarCell userId={file.createdById} />
            </td>
            <td style={{ whiteSpace: 'nowrap', color: '#64748B' }}>
                {file.modifiedAt ? Utils.formatDate(file.modifiedAt) : '—'}
            </td>
            <td>
                <UserAvatarCell userId={file.modifiedById} />
            </td>
            <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {isEditing ? (
                        <>
                            <RowIconButton
                                icon={faCheck}
                                title="Uložiť"
                                disabled={saving}
                                onClick={() => void saveEditing()}
                                activeColor="#16A34A"
                                activeBackground="#F0FDF4"
                            />
                            <RowIconButton
                                icon={faXmark}
                                title="Zrušiť"
                                disabled={saving}
                                onClick={cancelEditing}
                                activeColor="#EF4444"
                                activeBackground="#FEF2F2"
                            />
                        </>
                    ) : (
                        <>
                            <a
                                href={file.filePath}
                                target="_blank"
                                rel="noreferrer"
                                title="Stiahnuť"
                                onMouseEnter={() => setHovered(true)}
                                onMouseLeave={() => setHovered(false)}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '7px',
                                    border: `1.5px solid ${hovered ? accentColor : '#E2E8F0'}`,
                                    color: hovered ? accentColor : '#94A3B8',
                                    textDecoration: 'none',
                                    transition: 'border-color 0.15s, color 0.15s',
                                }}
                            >
                                <FontAwesomeIcon icon={faDownload} style={{ fontSize: '0.72rem' }} />
                            </a>
                            <RowIconButton icon={faPen} title="Premenovať" onClick={startEditing} activeColor={accentColor} activeBackground={accentColor + '12'} />
                            <DeleteRowButton onClick={onDelete} />
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
};

const RowIconButton: React.FC<{
    icon: any;
    title: string;
    onClick: () => void;
    activeColor: string;
    activeBackground: string;
    disabled?: boolean;
}> = ({ icon, title, onClick, activeColor, activeBackground, disabled }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            title={title}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '7px',
                border: `1.5px solid ${hovered ? activeColor : '#E2E8F0'}`,
                backgroundColor: hovered ? activeBackground : 'transparent',
                color: hovered ? activeColor : '#94A3B8',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                transition: 'border-color 0.15s, background-color 0.15s, color 0.15s',
            }}
        >
            <FontAwesomeIcon icon={icon} style={{ fontSize: '0.72rem' }} />
        </button>
    );
};

const DeleteRowButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            title="Zmazať"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '7px',
                border: `1.5px solid ${hovered ? '#EF4444' : '#E2E8F0'}`,
                backgroundColor: hovered ? '#FEF2F2' : 'transparent',
                color: hovered ? '#EF4444' : '#94A3B8',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background-color 0.15s, color 0.15s',
            }}
        >
            <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.72rem' }} />
        </button>
    );
};

function getFileStyle(ext: string): { fileIcon: any; iconColor: string } {
    switch (ext) {
        case 'pdf':  return { fileIcon: faFilePdf,   iconColor: '#EF4444' };
        case 'doc':
        case 'docx': return { fileIcon: faFileLines, iconColor: '#3B82F6' };
        case 'xls':
        case 'xlsx': return { fileIcon: faFileLines, iconColor: '#10B981' };
        case 'ppt':
        case 'pptx': return { fileIcon: faFileLines, iconColor: '#F97316' };
        case 'txt':
        case 'csv':  return { fileIcon: faFileLines, iconColor: '#8B5CF6' };
        default:     return { fileIcon: faFile,      iconColor: '#64748B' };
    }
}

export default FileTableComponent;
